import axios from 'axios';
import { AsyncAction } from '..';
import { AssetOverview } from '../../types/asset';

type State = {
    fetching: boolean;
    publish: 'unpublished' | 'publishing' | 'published';
    query?: string;
    sort?: { key: string; order?: 'ascending' | 'descending' };
    totalCount?: number;
    pageSize?: number;
    assets: AssetOverview[];
    error?: Error;
};

type Action = {
    type: 'asset_list/get';
    payload?: {
        query?: string;
        sort?: { key: string; order?: 'ascending' | 'descending' };
        index: number;
        pageSize: number;
        count: number;
        totalCount: number;
        assets: AssetOverview[];
    };
    error?: Error;
};

const initialState: State = {
    fetching: false,
    publish: 'unpublished',
    assets: [],
    totalCount: 0,
};

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'asset_list/get': {
            if (action.payload)
                return {
                    ...state,
                    fetching: false,
                    assets: action.payload.assets,
                    totalCount: action.payload.totalCount,
                    query: action.payload.query,
                    sort: action.payload.sort,
                };
            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true };
        }
        default:
            return state;
    }
};

/**
 * Send a request to the back-end to list or search assets that have matching code, name or type.
 *
 * @param params.query query string
 * @param params.index index of the page
 * @param cache reuse asset page if the query did not change and the page is already available
 * @param rethrow throw the internal error, causing the promise generated by the dispatch to fail
 */
const get = (
    params: {
        query?: string;
        sort?: { key: string; order?: 'ascending' | 'descending' };
        index?: number;
        pageSize?: number;
        extraParams?: { [param: string]: string };
    } = {},
    rethrow = false,
): AsyncAction => async (dispatch, getState) => {
    const { asset_list } = getState();
    params.query = params.query ?? asset_list.query;
    params.sort = params.sort ?? asset_list.sort;
    params.pageSize = params.pageSize ?? asset_list.pageSize;
    const search = params.query;
    const page = (params.index ?? 0) + 1;
    const ordering = params.sort?.order
        ? (params.sort?.order === 'descending' ? '-' : '') + `${params.sort?.key}`
        : undefined;
    try {
        dispatch({ type: 'asset_list/get' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'asset_list/get': cancel } });
        const response = await axios.get<{ results: AssetOverview[]; count: number } | []>('/assets/overview/', {
            params: { search, page, ordering, page_size: params.pageSize ?? 10, ...params.extraParams },
            cancelToken: cancel.token,
        });
        const count =
            (params.index ?? 0) === 0 && !(response.data instanceof Array)
                ? Math.ceil(response.data.count / response.data.results.length)
                : 0;
        dispatch({
            type: 'asset_list/get',
            payload: {
                query: params.query,
                sort: params.sort,
                index: params.index ?? 0,
                pageSize: params.pageSize ?? 10,
                count,
                totalCount: response.data instanceof Array ? 0 : response.data.count,
                assets: response.data instanceof Array ? [] : response.data.results,
            },
        });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'asset_list/get', error });
        if (rethrow) throw error;
    }
};

export const creators = {
    get,
};
